// widgets/chart/ui/ActiveColumn.tsx
import {FC, useEffect, useMemo, useRef, useState, useCallback} from 'react';
import styled from 'styled-components';
import {
  chartValue,
  LegendItem,
  PeriodKeyForFill,
  XDomain,
  XTickFormatter,
} from 'widgets/chart/model/types';
import {ChartTooltip, TooltipData, TooltipTextItem} from './ChartTooltip';
import {COLORS as COLORS_THEME} from '../../../shared/consts';

const COLUMN_ANIM_MS = 500;
const COLUMN_STAGGER_MS = 35;
const TOOLTIP = {width: 167, height: 96, gap: 8};
const TOOLTIP_FADE_MS = 150;
export const HOVER_LINE_COLOR = 'rgba(0,0,0,.3)';

const TOKENS = {
  color: {
    bar: 'var(--accent-muted, rgba(255, 114, 65, 1))',
    barLast: 'var(--accent-soft, rgba(255, 114, 65, 1))',
    diffPos: ' var(--accent-default, rgba(255, 79, 18, 1)',
    diffNeg: COLORS_THEME.BG.SURFACE3,
    grid: 'var(--neutral-container-soft, rgba(88, 93, 105, 0.05)',
    axis: 'rgba(0, 0, 0, 0)',
    marks: '#6B8AFF',
    threshold: '#FF4F12',
    text: 'var(--fg-muted, rgba(16, 24, 40, 0.55))',
    hoverLine: HOVER_LINE_COLOR,
  },
  size: {padL: 8, padR: 12, padT: 8, padB: 24},
  text: {fontSize: 10, fontWeight: 400 as const, lineHeight: 16, letterSpacing: 0.4},
};

const Wrap = styled.div<{w: string | number; h: number | string}>`
  position: relative;
  width: ${({w}) => (typeof w === 'number' ? `${w}px` : w)};
  height: ${({h}) => (typeof h === 'number' ? `${h}px` : h)};
  display: flex;
  flex-direction: column;
`;
const Stage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;
const Overlay = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
`;
const AxisLabel = styled.div`
  position: absolute;
  transform: translate(-50%, 0);
  color: ${TOKENS.color.text};
  font-size: ${TOKENS.text.fontSize}px;
  font-weight: ${TOKENS.text.fontWeight};
  letter-spacing: ${TOKENS.text.letterSpacing}px;
  line-height: ${TOKENS.text.lineHeight}px;
  white-space: nowrap;
  pointer-events: none;
  text-align: center;
`;
const AxisLabelY = styled(AxisLabel)`
  transform: translate(0, -50%);
  left: calc(100% - ${TOKENS.size.padR - 2}px);
`;
const FadeTooltip = styled.div<{visible: boolean}>`
  opacity: ${({visible}) => (visible ? 1 : 0)};
  transition: opacity ${TOOLTIP_FADE_MS}ms ease;
`;

function useMeasure() {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<{w: number; h: number}>({w: 0, h: 0});
  const ref = useCallback((el: HTMLDivElement | null) => {
    setNode((prev) => (prev === el ? prev : el));
  }, []);
  useEffect(() => {
    if (!node) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setRect({w: r.width, h: r.height});
    });
    ro.observe(node);
    const rr = node.getBoundingClientRect();
    setRect({w: rr.width, h: rr.height});
    return () => ro.disconnect();
  }, [node]);
  return {ref, rect};
}
function inferXUnit(stepMs: number): 'hour' | 'day' | 'week' | 'month' {
  const h = 3600_000;
  const d = 24 * h;
  if (stepMs <= 2 * h) return 'hour';
  if (stepMs <= 2 * d) return 'day';
  if (stepMs <= 14 * d) return 'week';
  return 'month';
}
function periodMsByKey(k: PeriodKeyForFill): number {
  const h = 3600_000;
  const d = 24 * h;
  if (k === 'day') return d;
  if (k === 'week') return 7 * d;
  return 30 * d;
}
const ddmm = (d: Date) =>
  `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
const hhmm = (d: Date) =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

function placeTooltip(x: number, yTop: number, viewWidth: number) {
  const minLeft = 0;
  const maxLeft = Math.max(0, viewWidth - TOOLTIP.width);
  const rawLeft = x - TOOLTIP.width / 2;
  const left = Math.min(Math.max(rawLeft, minLeft), maxLeft);
  let arrowX = x - left;
  arrowX = Math.max(6, Math.min(TOOLTIP.width - 6, arrowX));
  const top = yTop - TOOLTIP.height - TOOLTIP.gap;
  return {left, top, arrowX};
}

type Props = {
  data: chartValue[];
  seriesLabel: string;
  width?: string | number;
  height?: number | string;
  legend?: LegendItem[];
  marks?: number[];
  thresholds?: number[];
  showAxes?: boolean;
  fillFuture?: boolean;
  periodForFill?: PeriodKeyForFill;
  highlightLast?: boolean;
  showDiff?: boolean;
  diffLegendLabels?: {pos: string; neg: string};
  onLegend?: (items: LegendItem[]) => void;
  tooltipText: TooltipTextItem[];
  xTicks?: number[];
  xTickFormatter?: XTickFormatter;
  xDomain?: XDomain;
  currentDate?: number;
};

export const ActiveColumn: FC<Props> = ({
  data,
  seriesLabel,
  width = '100%',
  height = '100%',
  legend,
  marks,
  thresholds,
  showAxes = true,
  fillFuture = true,
  periodForFill = 'day',
  highlightLast = false,
  showDiff = false,
  diffLegendLabels = {pos: 'Прирост', neg: 'Падение'},
  onLegend,
  tooltipText,
  xTicks: externalXTicks,
  xTickFormatter,
  xDomain,
  currentDate,
}) => {
  const meas = useMeasure();
  const svgRef = useRef<SVGSVGElement | null>(null);

  const vw = Math.max(1, meas.rect.w || 600);
  const vh = Math.max(1, meas.rect.h || (typeof height === 'number' ? height : 240));

  const {padL, padR, padT, padB} = TOKENS.size;

  const real = data.filter((d) => d.y != null);

  const stepX = real.length > 1 ? real[1].x - real[0].x : 3600_000;
  const xUnit = inferXUnit(stepX);

  const xsReal = real.map((d) => d.x);
  const minXData = xsReal.length ? Math.min(...xsReal) : 0;
  const maxXData = xsReal.length ? Math.max(...xsReal) : minXData;
  const desiredEnd = fillFuture ? minXData + periodMsByKey(periodForFill) : maxXData;
  const isCurrentDateMode = currentDate !== undefined;
  const [minXFixed, maxXFixed] = useMemo(() => {
    if (isCurrentDateMode) {
      const start24h = (currentDate as number) - 24 * 3600_000;
      return [start24h, currentDate as number];
    }
    const minX = xDomain?.min ?? minXData;
    const maxX = xDomain?.max ?? Math.max(maxXData + stepX, desiredEnd);
    return [minX, maxX];
  }, [
    isCurrentDateMode,
    currentDate,
    xDomain?.min,
    xDomain?.max,
    minXData,
    maxXData,
    stepX,
    desiredEnd,
  ]);
  const realForRender = useMemo(
    () => (isCurrentDateMode ? real.filter((d) => d.x >= minXFixed && d.x <= maxXFixed) : real),
    [isCurrentDateMode, real, minXFixed, maxXFixed]
  );

  const ysNum = real.map((d) => d.y as number);
  const avgY = ysNum.reduce((a, b) => a + b, 0) / (ysNum.length || 1);
  let minY = Math.min(...(ysNum.length ? ysNum : [0])) - avgY * 0.005;
  let maxY = Math.max(...(ysNum.length ? ysNum : [1])) + avgY * 0.005;
  if (!(isFinite(minY) && isFinite(maxY)) || minY === maxY) {
    minY = minY - 1;
    maxY = maxY + 1;
  }

  const baseInnerW = vw - padL - padR;
  const slots = Math.max(1, Math.round((maxXFixed - minXFixed) / stepX)); // учитываем весь домен, а не только real.length
  const groupW = Math.max(6, baseInnerW / (slots * 1.5));
  const padLx = padL;
  const padRx = padR;
  const innerW = vw - padLx - padRx;
  const xScale = (x: number) => padLx + ((x - minXFixed) / (maxXFixed - minXFixed || 1)) * innerW;
  const barPadL = padL + groupW / 2;
  const barPadR = padR + groupW / 2;
  const barInnerW = vw - barPadL - barPadR;
  const xScaleBar = (x: number) =>
    barPadL + ((x - minXFixed) / (maxXFixed - minXFixed || 1)) * barInnerW;
  const yScale = (y: number) => vh - padB - ((y - minY) / (maxY - minY || 1)) * (vh - padT - padB);

  const [enter, setEnter] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setEnter(false);
    setTip(null);
    const id = requestAnimationFrame(() => setEnter(true));
    return () => cancelAnimationFrame(id);
  }, [
    JSON.stringify(realForRender.map((d) => d.y)),
    realForRender.length,
    fillFuture,
    periodForFill,
    vw,
    vh,
  ]);

  const yTicks = useMemo(() => {
    const res: number[] = [];
    const step = (maxY - minY) / 5;
    for (let i = 0; i <= 5; i++) res.push(minY + i * step);
    return res;
  }, [minY, maxY]);

  const autoXTicks = useMemo(() => {
    const tickCount = 5;
    const step = (maxXFixed - minXFixed) / (tickCount - 1 || 1);
    return Array.from({length: tickCount}, (_, i) => minXFixed + i * step);
  }, [minXFixed, maxXFixed]);

  const xTicks = useMemo(() => {
    if (isCurrentDateMode) {
      const start = minXFixed;
      const end = maxXFixed;
      const ticks: number[] = [start];

      const nextSixHourBoundary = (ts: number) => {
        const d = new Date(ts);
        d.setHours(Math.floor(d.getHours() / 6) * 6, 0, 0, 0);
        if (d.getTime() <= ts) d.setHours(d.getHours() + 6);
        return d.getTime();
      };

      let t = nextSixHourBoundary(start);
      while (t < end) {
        ticks.push(t);
        t += 6 * 3600_000;
      }
      if (ticks[ticks.length - 1] !== end) ticks.push(end);
      return ticks;
    }

    if (externalXTicks && externalXTicks.length > 1) return externalXTicks;
    if (realForRender.length === 0) return autoXTicks;

    const firstX = realForRender[0].x;
    const lastX = maxXFixed;
    const tickCount = xUnit === 'hour' ? 6 : 5;
    const step = (lastX - firstX) / (tickCount - 1 || 1);

    const ticks = Array.from({length: tickCount}, (_, i) => firstX + i * step);
    ticks[0] = firstX;
    ticks[ticks.length - 1] = Math.min(ticks[ticks.length - 1], lastX);
    return ticks.filter((x) => x >= minXFixed && x <= maxXFixed);
  }, [isCurrentDateMode, externalXTicks, realForRender, autoXTicks, maxXFixed, xUnit, minXFixed]);

  const fmtXParts = useCallback(
    (ms: number) => {
      const d = new Date(ms);
      const today = new Date();

      if (xUnit === 'hour') {
        if (isSameDay(d, today)) return [hhmm(d)];
        return [`${hhmm(d)}`, ddmm(d)];
      }

      if (xUnit === 'week') {
        const start = new Date(d.getTime());
        const end = new Date(start.getTime() + 6 * 24 * 3600_000);
        return [`${ddmm(start)}`, `${ddmm(end)}`];
      }

      return [ddmm(d)];
    },
    [xUnit]
  );

  const formatTick = useCallback(
    (value: number, index: number, ticks: number[]) => {
      if (isCurrentDateMode) return [hhmm(new Date(value))];
      if (xTickFormatter) return xTickFormatter(value, index, ticks);
      return fmtXParts(value);
    },
    [isCurrentDateMode, xTickFormatter, fmtXParts]
  );
  const xLabels = useMemo(() => {
    const minGap = 4;
    const labels = xTicks.map((t, i) => {
      const parts = formatTick(t, i, xTicks);
      const maxLineLength = parts.reduce((max, part) => Math.max(max, part.length), 0);
      const width = Math.max(20, maxLineLength * (TOKENS.text.fontSize * 0.45));
      const half = width / 2;
      const rawX = xScale(t);
      const minX = padLx + half;
      const maxX = vw - padRx - half;
      const x = Math.max(minX, Math.min(rawX, maxX));
      return {t, parts, half, x, minX, maxX};
    });

    if (labels.length <= 2) {
      if (labels.length >= 1) labels[0].x = xScale(labels[0].t);
      if (labels.length === 2) labels[1].x = xScale(labels[1].t);
      return labels;
    }

    const lastIdx = labels.length - 1;
    const firstFixed = xScale(labels[0].t);
    const lastFixed = xScale(labels[lastIdx].t);

    for (let i = 1; i < lastIdx; i++) {
      const prev = labels[i - 1];
      const cur = labels[i];
      const minCenter = prev.x + prev.half + minGap + cur.half;
      if (cur.x < minCenter) cur.x = minCenter;
      if (cur.x > cur.maxX) cur.x = cur.maxX;
    }

    for (let i = lastIdx - 1; i >= 1; i--) {
      const next = labels[i + 1];
      const cur = labels[i];
      const maxCenter = next.x - next.half - minGap - cur.half;
      if (cur.x > maxCenter) cur.x = maxCenter;
      if (cur.x < cur.minX) cur.x = cur.minX;
    }

    labels[0].x = firstFixed;
    labels[lastIdx].x = lastFixed;

    return labels;
  }, [xTicks, formatTick, xScale, padLx, padRx, vw]);

  const fmtY = (v: number) => {
    const range = Math.abs(maxY - minY);
    return range >= 10 ? Math.round(v).toString() : v.toFixed(1);
  };

  const legendAuto: LegendItem[] = useMemo(() => {
    const baseSeries: LegendItem[] = [{color: TOKENS.color.bar, label: seriesLabel}];
    const markItems = (marks ?? []).map((v) => ({color: TOKENS.color.marks, label: `Метка ${v}`}));
    const diffItems = showDiff
      ? [
          {color: TOKENS.color.diffPos, label: diffLegendLabels.pos},
          {color: TOKENS.color.diffNeg, label: diffLegendLabels.neg},
        ]
      : [];
    const extra = legend ?? [];
    return [...baseSeries, ...markItems, ...diffItems, ...extra];
  }, [seriesLabel, marks, showDiff, diffLegendLabels, legend]);

  useEffect(() => {
    if (onLegend) onLegend(legendAuto);
  }, [onLegend, legendAuto]);

  const [tip, setTip] = useState<{
    left: number;
    top: number;
    arrowX: number;
    xPix: number;
    data: TooltipData;
  } | null>(null);

  function showTipForBar(cx: number, yTop: number, idx: number) {
    if (!showAxes) return;
    const pos = placeTooltip(cx, yTop, vw);
    const cur = realForRender[idx];
    const prev = idx > 0 ? realForRender[idx - 1] : undefined;
    setTip({
      left: pos.left,
      top: pos.top,
      arrowX: pos.arrowX,
      xPix: cx,
      data: {
        type: 'column',
        current: {ts: cur.x, value: (cur.y as number) ?? null},
        prev: prev ? {ts: prev.x, value: (prev.y as number) ?? null} : undefined,
        tooltipText,
      },
    });
    setVisible(true);
  }

  const showTipAtX = (xp: number) => {
    if (!showAxes || realForRender.length === 0) return;
    const rect = (svgRef.current as SVGSVGElement).getBoundingClientRect();
    const relX = xp - rect.left;
    const xVal = ((relX - padLx) / (innerW || 1)) * (maxXFixed - minXFixed) + minXFixed;
    let bestIdx = 0;
    let bestD = Infinity;
    for (let i = 0; i < realForRender.length; i++) {
      const d = Math.abs(realForRender[i].x - xVal);
      if (d < bestD) {
        bestD = d;
        bestIdx = i;
      }
    }
    const cur = realForRender[bestIdx];
    const prev = bestIdx > 0 ? realForRender[bestIdx - 1] : undefined;
    const cx = xScaleBar(cur.x);
    const top = Math.min(yScale(Math.max(minY, cur.y as number)), yScale(minY));
    const pos = placeTooltip(cx, top, vw);
    setTip({
      left: pos.left,
      top: pos.top,
      arrowX: pos.arrowX,
      xPix: cx,
      data: {
        type: 'column',
        current: {ts: cur.x, value: (cur.y as number) ?? null},
        prev: prev ? {ts: prev.x, value: (prev.y as number) ?? null} : undefined,
        tooltipText,
      },
    });
    setVisible(true);
  };

  const onSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    showTipAtX(e.clientX);
  };

  const onSvgMouseLeave = () => {
    setVisible(false);
    setTimeout(() => setTip(null), TOOLTIP_FADE_MS);
  };

  const visibleMarks = useMemo(
    () => (marks ?? []).filter((m) => m >= minY && m <= maxY),
    [marks, minY, maxY]
  );
  const visibleThresholds = useMemo(
    () => (thresholds ?? []).filter((t) => t >= minY && t <= maxY),
    [thresholds, minY, maxY]
  );

  return (
    <Wrap w={width} h={height}>
      <Stage ref={meas.ref}>
        <svg
          ref={svgRef}
          width={vw}
          height={vh}
          style={{display: 'block'}}
          onMouseMove={onSvgMouseMove}
          onMouseLeave={onSvgMouseLeave}
        >
          <rect x={0} y={0} width={vw} height={vh} fill="transparent" />
          {showAxes && (
            <>
              <line
                x1={padLx}
                x2={vw - padRx}
                y1={vh - padB}
                y2={vh - padB}
                stroke={TOKENS.color.axis}
                strokeWidth={1}
              />
              {yTicks.map((t, i) => (
                <line
                  key={`yg-${i}`}
                  x1={padLx}
                  x2={vw - padRx}
                  y1={yScale(t)}
                  y2={yScale(t)}
                  stroke={TOKENS.color.grid}
                  strokeWidth={1}
                />
              ))}
              {xTicks.map((t, i) => {
                const x = xScale(t);
                return (
                  <line
                    key={`xg-${i}`}
                    x1={x}
                    x2={x}
                    y1={padT}
                    y2={vh - padB}
                    stroke={TOKENS.color.grid}
                    strokeWidth={1}
                  />
                );
              })}
            </>
          )}

          {realForRender.map((d, i) => {
            const x = xScaleBar(d.x) - groupW / 2;
            const y = yScale(Math.max(minY, d.y as number));
            const y0 = yScale(minY);
            const isLast = i === realForRender.length - 1;
            const color = highlightLast && isLast ? TOKENS.color.barLast : TOKENS.color.bar;
            const h = Math.abs(y0 - y);
            const opacity = highlightLast ? (isLast ? 1 : 0.4) : 1;
            const center = x + groupW / 2;
            const top = Math.min(y, y0);
            return (
              <rect
                key={i}
                x={x}
                y={Math.min(y, y0)}
                width={groupW}
                height={h}
                fill={color}
                opacity={opacity}
                style={{
                  transformBox: 'fill-box',
                  transformOrigin: 'center bottom',
                  transform: enter ? 'scaleY(1)' : 'scaleY(0)',
                  transition: `transform ${COLUMN_ANIM_MS}ms ease ${i * COLUMN_STAGGER_MS}ms, opacity 160ms ease`,
                  cursor: showAxes ? 'pointer' : 'default',
                }}
                onMouseEnter={() => showAxes && showTipForBar(center, top, i)}
              />
            );
          })}

          {showDiff &&
            realForRender.map((d, i) => {
              if (i === 0) return null;
              if (realForRender[i - 1].y == null || d.y == null) return null;
              const prevVal = realForRender[i - 1].y as number;
              const diff = (d.y as number) - prevVal;
              if (diff === 0) return null;
              const positive = diff > 0;
              const baseFrom = positive ? prevVal : (d.y as number);
              const topTo = positive ? (d.y as number) : prevVal;
              const yFrom = yScale(baseFrom);
              const yTo = yScale(topTo);
              const h2 = Math.abs(yFrom - yTo);
              const y2 = Math.min(yFrom, yTo);
              const x = xScaleBar(d.x) - groupW / 2;
              return (
                <rect
                  key={`diff-${i}`}
                  x={x}
                  y={y2}
                  width={groupW}
                  height={h2}
                  fill={positive ? TOKENS.color.diffPos : TOKENS.color.diffNeg}
                  style={{
                    transformBox: 'fill-box',
                    transformOrigin: 'center bottom',
                    transform: enter ? 'scaleY(1)' : 'scaleY(0)',
                    transition: `transform ${COLUMN_ANIM_MS}ms ease ${i * COLUMN_STAGGER_MS}ms`,
                  }}
                />
              );
            })}

          {visibleMarks.map((m, i) => (
            <line
              key={`m-${i}`}
              x1={padLx}
              x2={vw - padRx}
              y1={yScale(m)}
              y2={yScale(m)}
              stroke={TOKENS.color.marks}
              strokeDasharray="6 6"
              strokeWidth={1}
            />
          ))}
          {visibleThresholds.map((t, i) => (
            <line
              key={`thr-${i}`}
              x1={padLx}
              x2={vw - padRx}
              y1={yScale(t)}
              y2={yScale(t)}
              stroke={TOKENS.color.threshold}
              strokeWidth={1}
            />
          ))}

          {showAxes && tip && (
            <line
              x1={tip.xPix}
              x2={tip.xPix}
              y1={padT}
              y2={vh - padB}
              stroke={TOKENS.color.hoverLine}
              strokeWidth={1}
              strokeDasharray="4 2"
              pointerEvents="none"
            />
          )}
        </svg>

        <Overlay>
          {showAxes &&
            yTicks.map((t, i) => (
              <AxisLabelY key={`yl-${i}`} style={{top: yScale(t)}}>
                {fmtY(t)}
              </AxisLabelY>
            ))}
          {showAxes &&
            xLabels.map((label, i) => {
              return (
                <AxisLabel key={`xl-${i}`} style={{left: label.x, top: vh - padB + 2}}>
                  <>
                    {label.parts[0]}
                    {label.parts[1] ? (
                      <>
                        <br />
                        {label.parts[1]}
                      </>
                    ) : null}
                  </>
                </AxisLabel>
              );
            })}
          {showAxes && tip && (
            <FadeTooltip visible={visible}>
              <ChartTooltip
                data={tip.data}
                tooltipText={tooltipText}
                style={{left: tip.left, top: tip.top, ['--arrow-x']: `${tip.arrowX}px`}}
              />
            </FadeTooltip>
          )}
        </Overlay>
      </Stage>
    </Wrap>
  );
};
